import * as TabsPrimitive from "@radix-ui/react-tabs";
import * as React from "react";

import { cn } from "@/lib/ui/cn";

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      "tablet:typography-heading-3 typography-heading-2 inline-flex items-center gap-6",
      className,
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "focus-visible:ring-ring inline-flex cursor-pointer items-center justify-center transition-all focus-visible:ring-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50",
      "data-[state=active]:text-text-alternate data-[state=inactive]:text-text-subtle hover:text-text-alternate!",
      className,
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

type TabsContentProps = React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content> & {
  /**
   * If true, keep the content mounted and just hide inactive states.
   * Useful for things like HubSpot forms that you don't want to re-initialize.
   */
  keepMounted?: boolean;
};

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  TabsContentProps
>(({ className, keepMounted = false, forceMount, ...props }, ref) => {
  const shouldForceMount = keepMounted || !!forceMount;

  return (
    <TabsPrimitive.Content
      ref={ref}
      forceMount={shouldForceMount ? true : undefined}
      className={cn(
        "focus-visible:outline-none",
        // When we keep it mounted, hide inactive panels via data-state
        keepMounted && "data-[state=inactive]:hidden",
        className,
      )}
      {...props}
    />
  );
});
TabsContent.displayName = TabsPrimitive.Content.displayName;

const TabsIndicators = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn("py-padding-element-lg flex w-full gap-1", className)}
    {...props}
  />
));
TabsIndicators.displayName = "TabsIndicators";

const TabsIndicator = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "data-[state=active]:bg-emerald-light tablet:max-w-20 h-1 w-full max-w-[79px] cursor-pointer rounded-sm bg-white/60 transition-colors",
      className,
    )}
    {...props}
  />
));
TabsIndicator.displayName = "TabsIndicator";

export { Tabs, TabsContent, TabsIndicator, TabsIndicators, TabsList, TabsTrigger };
